import { useState, useEffect } from "react";
import { Bell, Target } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export type GoalBehavior = "auto_stop" | "alert_only";

interface TimeGoalInputProps {
  goalMinutes: number | null;
  onGoalChange: (minutes: number | null) => void;
  goalBehavior: GoalBehavior;
  onBehaviorChange: (behavior: GoalBehavior) => void;
  disabled?: boolean;
}

const PRESETS = [
  { label: "15min", minutes: 15 },
  { label: "30min", minutes: 30 },
  { label: "1h", minutes: 60 },
  { label: "2h", minutes: 120 },
];

export function TimeGoalInput({
  goalMinutes,
  onGoalChange,
  goalBehavior,
  onBehaviorChange,
  disabled,
}: TimeGoalInputProps) {
  const [hours, setHours] = useState("");
  const [minutes, setMinutes] = useState("");

  // Sync from goalMinutes prop
  useEffect(() => {
    if (goalMinutes !== null && goalMinutes > 0) {
      const h = Math.floor(goalMinutes / 60);
      const m = goalMinutes % 60;
      setHours(h > 0 ? h.toString() : "");
      setMinutes(m > 0 ? m.toString() : "");
    } else {
      setHours("");
      setMinutes("");
    }
  }, [goalMinutes]);

  const updateGoal = (h: string, m: string) => {
    const hoursNum = parseInt(h, 10) || 0;
    const minutesNum = parseInt(m, 10) || 0;
    const total = hoursNum * 60 + minutesNum;
    
    if (total > 0 && total <= 24 * 60) {
      onGoalChange(total);
    } else if (h === "" && m === "") {
      onGoalChange(null);
    }
  };

  const handleHoursChange = (value: string) => {
    // Only allow numbers 0-24
    const cleaned = value.replace(/\D/g, "").slice(0, 2);
    const num = parseInt(cleaned, 10);
    if (cleaned === "" || (num >= 0 && num <= 24)) {
      setHours(cleaned);
      updateGoal(cleaned, minutes);
    }
  };

  const handleMinutesChange = (value: string) => {
    // Only allow numbers 0-59
    const cleaned = value.replace(/\D/g, "").slice(0, 2);
    const num = parseInt(cleaned, 10);
    if (cleaned === "" || (num >= 0 && num <= 59)) {
      setMinutes(cleaned);
      updateGoal(hours, cleaned);
    }
  };

  const handlePreset = (mins: number) => {
    onGoalChange(mins);
  };

  const handleClear = () => {
    setHours("");
    setMinutes("");
    onGoalChange(null);
  };

  const hasGoal = goalMinutes !== null && goalMinutes > 0;

  return (
    <div className="space-y-2">
      <Label className="text-sm text-muted-foreground flex items-center gap-1.5">
        <Target className="h-3.5 w-3.5" />
        Meta de Tempo
      </Label>
      
      <div className="flex items-center gap-2">
        {/* Hours and Minutes inputs */}
        <div className="flex items-center gap-1 flex-1">
          <div className="relative flex-1">
            <Input
              value={hours}
              onChange={(e) => handleHoursChange(e.target.value)}
              placeholder="0"
              className="text-center font-mono tabular-nums pr-6"
              disabled={disabled}
              inputMode="numeric"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              h
            </span>
          </div>
          <span className="text-muted-foreground font-medium">:</span>
          <div className="relative flex-1">
            <Input
              value={minutes}
              onChange={(e) => handleMinutesChange(e.target.value)}
              placeholder="00"
              className="text-center font-mono tabular-nums pr-8"
              disabled={disabled}
              inputMode="numeric"
            />
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground pointer-events-none">
              min
            </span>
          </div>
        </div>

        {/* Settings button */}
        <Popover>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button 
                  type="button"
                  variant={hasGoal ? "secondary" : "ghost"} 
                  size="icon" 
                  className="h-10 w-10 shrink-0"
                  disabled={disabled}
                >
                  <Bell className={`h-4 w-4 ${hasGoal ? "text-primary" : ""}`} />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>Configurar meta</p>
            </TooltipContent>
          </Tooltip>
          <PopoverContent className="w-72" align="end" sideOffset={8}>
            <div className="space-y-4">
              {/* Quick presets */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium">Atalhos rápidos</h4>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map((preset) => (
                    <Button
                      key={preset.minutes}
                      type="button"
                      variant={goalMinutes === preset.minutes ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePreset(preset.minutes)}
                      className="text-xs"
                    >
                      {preset.label}
                    </Button>
                  ))}
                  {hasGoal && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleClear}
                      className="text-xs text-muted-foreground"
                    >
                      Limpar
                    </Button>
                  )}
                </div>
              </div>

              {/* Behavior options */}
              <div className="space-y-2 pt-2 border-t">
                <h4 className="text-sm font-medium">Ao atingir a meta</h4>
                <RadioGroup
                  value={goalBehavior}
                  onValueChange={(v) => onBehaviorChange(v as GoalBehavior)}
                  className="gap-2"
                >
                  <label 
                    htmlFor="auto_stop" 
                    className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                      goalBehavior === "auto_stop" 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <RadioGroupItem value="auto_stop" id="auto_stop" />
                    <div className="space-y-0.5">
                      <span className="text-sm font-medium">Parar automaticamente</span>
                      <p className="text-xs text-muted-foreground">Timer para ao atingir</p>
                    </div>
                  </label>
                  <label 
                    htmlFor="alert_only" 
                    className={`flex items-center gap-3 rounded-lg border p-2.5 cursor-pointer transition-colors ${
                      goalBehavior === "alert_only" 
                        ? "border-primary bg-primary/5" 
                        : "border-border hover:bg-muted/50"
                    }`}
                  >
                    <RadioGroupItem value="alert_only" id="alert_only" />
                    <div className="space-y-0.5">
                      <span className="text-sm font-medium">Apenas alertar</span>
                      <p className="text-xs text-muted-foreground">Continua contando</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      {hasGoal && (
        <p className="text-xs text-muted-foreground">
          {goalBehavior === "auto_stop" ? "⏱️ Para automaticamente" : "🔔 Alerta ao atingir"}
        </p>
      )}
    </div>
  );
}
